import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Clock, XCircle, RefreshCw, LogOut, Loader2 } from 'lucide-react';

const StudentPending = () => {
  const navigate = useNavigate();
  const { student, isStudent, isPendingStudent, isRejectedStudent, isApprovedStudent, refreshStudentStatus, logout } = useAuth();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!isStudent) {
      navigate('/student/login');
      return;
    }
    
    if (isApprovedStudent) {
      navigate('/student/exams');
    }
  }, [isStudent, isApprovedStudent, navigate]);

  const handleCheckStatus = async () => {
    setChecking(true);
    const updatedStudent = await refreshStudentStatus();
    setChecking(false);
    
    if (updatedStudent?.status === 'approved') {
      navigate('/student/exams');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isStudent) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex flex-col">
      {/* Header */}
      <header className="bg-[#FDF8F0] border-b border-[#E8DFCF] py-4 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
            <span className="text-xl font-bold text-[#412402]">المعتمد في اللغة العربية</span>
          </Link>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleLogout}
            className="border-[#E8DFCF] text-[#854F0B] hover:bg-[#EF9F27]/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 ml-2" />
            خروج
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-[#FDF8F0] border border-[#E8DFCF] shadow-xl">
          <CardContent className="p-8">
            {isPendingStudent ? (
              // Pending Status
              <div className="text-center">
                <div className="w-20 h-20 bg-[#EF9F27]/15 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-[#EF9F27]" />
                </div>
                <h1 className="text-2xl font-bold text-[#412402] mb-4">
                  حسابك قيد المراجعة
                </h1>
                <div className="bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-xl p-6 mb-6">
                  <p className="text-[#6B4C2A] text-lg leading-relaxed">
                    تم استلام طلب التسجيل بنجاح.
                    <br />
                    حسابك قيد المراجعة من قبل المعلم، وسيتم تفعيل الدخول بعد الموافقة.
                  </p>
                </div>
                
                <div className="bg-[#FFFFFF] rounded-xl p-4 mb-6 border border-[#E8DFCF]">
                  <h3 className="font-semibold text-[#412402] mb-2">بياناتك</h3>
                  <div className="text-sm text-[#6B4C2A] space-y-1">
                    <p>الاسم: {student?.name}</p>
                    <p>الصف: {student?.grade}</p>
                    <p>الشعبة: {student?.section}</p>
                    {student?.school && <p>المدرسة: {student?.school}</p>}
                  </div>
                </div>

                <Button 
                  onClick={handleCheckStatus}
                  variant="outline"
                  className="w-full border-[#E8DFCF] text-[#854F0B] hover:bg-[#EF9F27]/10"
                  disabled={checking}
                  data-testid="check-status-btn"
                >
                  {checking ? (
                    <>
                      <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                      جاري التحقق...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 ml-2" />
                      تحقق من حالة الحساب
                    </>
                  )}
                </Button>
              </div>
            ) : isRejectedStudent ? (
              // Rejected Status
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-[#412402] mb-4">
                  تم رفض طلب التسجيل
                </h1>
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                  <p className="text-red-800 text-lg leading-relaxed">
                    نأسف، لم تتم الموافقة على طلب التسجيل الخاص بك.
                    <br />
                    يرجى التواصل مع المعلم للمزيد من المعلومات.
                  </p>
                </div>
                
                <div className="bg-[#FFFFFF] rounded-xl p-4 mb-6 border border-[#E8DFCF]">
                  <h3 className="font-semibold text-[#412402] mb-2">بياناتك</h3>
                  <div className="text-sm text-[#6B4C2A] space-y-1">
                    <p>الاسم: {student?.name}</p>
                    <p>الصف: {student?.grade}</p>
                    <p>الشعبة: {student?.section}</p>
                  </div>
                </div>

                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full border-[#E8DFCF] text-[#412402] hover:bg-[#FDF8F0]"
                >
                  <LogOut className="w-5 h-5 ml-2" />
                  تسجيل الخروج
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default StudentPending;
